import _ from 'lodash';
import React from 'react';
import { reverse } from 'named-urls';
import { gql } from '@apollo/client';
import { Query } from '@apollo/react-components';
import routes from '@achievable-me/tundra-wizard/routes';
import { fullName } from '@achievable-me/tundra-wizard/contentHelpers';
import { evaluate, prepare } from '@achievable-me/tundra-wizard/evaluate';
import { parsePrompt } from '$lib/quiz';
import { parseMarkdown } from '$lib/markdown';
import SpinnerError from '$components/library/SpinnerError';
import Seo from '$components/Seo';
import ProductContentNotFound from '$components/ProductContent/ProductContentNotFound';
import { useTrack } from '$lib/tracking';
import { isBot } from '$lib/userAgent';
import styles from './styles.scss';

const CONTENT_VIEWER = gql`
  query ContentViewer($input: ContentViewerInput!) {
    content(input: $input) {
      uuid
      markdown
      numItems
    }
  }
`;

const GOOGLE_QUIZ_LINKS = gql`
  query ContentViewer($input: ContentViewerInput!) {
    content(input: $input) {
      uuid
      items {
        uuid
        code
        transpiled
      }
      parentNames
    }
  }
`;

const GoogleOnlyQuizShareLinks = (props) => {
  const isBotResult = isBot();

  if (!isBotResult) return null;

  const { contentInstance, productSlug, productName } = props;
  const { uuid, name: contentName } = contentInstance;

  return (
    <Query query={GOOGLE_QUIZ_LINKS} variables={{ input: { uuid } }}>
      {({ loading, error, data }) => {
        if (loading || error) {
          return (
            <div className={styles.spinnerContainer}>
              <SpinnerError error={error} />
            </div>
          );
        }

        const {
          content: { items, parentNames },
        } = data;

        if (!items.length) return null;

        const clonedParentNames = _.clone(parentNames);
        clonedParentNames.reverse();
        clonedParentNames.push(contentName);
        const fullContentName = clonedParentNames.join(' | ');

        return (
          <div className={styles.googleLinksContainer}>
            <h2>
              {productName} questions on {fullContentName}
            </h2>
            <ul>
              {items.map((item) => {
                const { transpiled, code } = item;
                const DEFAULT_SEED = '12345';
                const evaluated = prepare(evaluate(transpiled, { seed: DEFAULT_SEED }));
                const { prompt } = evaluated;

                const { title, truncatedSlug } = parsePrompt({
                  code,
                  prompt,
                });

                const href = `${reverse(routes.study.quiz, {
                  productSlug,
                  quizCode: code,
                })}#seed=${DEFAULT_SEED}&slug=${truncatedSlug}`;

                return (
                  <li key={code}>
                    <a href={href}>{title}</a>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      }}
    </Query>
  );
};

const ContentViewer = (props) => {
  const { contentInstance, product } = props;
  const { uuid } = contentInstance;
  const { slug: productSlug, name: productName } = product;
  useTrack('Topic | Viewed', { productSlug, contentUuid: uuid }, [uuid]);

  const contentByUuid = _.keyBy(product.content, 'uuid');

  return (
    <Query query={CONTENT_VIEWER} variables={{ input: { uuid } }}>
      {({ loading, error, data }) => {
        if (loading || error) {
          return (
            <div className={styles.spinnerContainer}>
              <SpinnerError error={error} />
            </div>
          );
        }

        const { content } = data;

        if (!content.markdown || _.isEmpty(content.markdown)) {
          return <ProductContentNotFound product={product} contentUuid={content.uuid} />;
        }

        const contentFullName = fullName({ content: contentInstance, contentByUuid });

        const fragmentUuid = `Content:${content.uuid}`;
        const { markdownHtml, markdownTextSnippet } = parseMarkdown(content.markdown, {
          fragmentUuid,
        });

        return (
          <>
            <Seo
              title={contentFullName}
              canonical={reverse(routes.study.learn, {
                productSlug: product.slug,
                contentUuid: contentInstance.slug,
              })}
              description={markdownTextSnippet}
            />
            {markdownHtml}
            <GoogleOnlyQuizShareLinks
              contentInstance={contentInstance}
              productSlug={productSlug}
              productName={productName}
            />
          </>
        );
      }}
    </Query>
  );
};

export default ContentViewer;
